'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ChartDataPoint } from '@/lib/chart-utils';
import { logger } from '@/lib/logger';

interface Datapack {
  id: string;
  data: ChartDataPoint[];
}

interface DatapackContextType {
  datapacks: Map<string, Datapack>;
  setPrefetchedDatapacks: (datapacks: Datapack[]) => void;
  getDatapack: (id: string) => Datapack | undefined;
  clearDatapacks: () => void;
}

const DatapackContext = createContext<DatapackContextType | undefined>(undefined);

export function DatapackProvider({ children }: { children: ReactNode }) {
  const [datapacks, setDatapacks] = useState<Map<string, Datapack>>(new Map());

  const setPrefetchedDatapacks = useCallback((prefetched: Datapack[]) => {
    logger.info(`[DatapackContext] Received ${prefetched.length} datapacks to store:`, prefetched);
    setDatapacks((currentDatapacks) => {
      const newMap = new Map(currentDatapacks);
      prefetched.forEach((dp) => {
        logger.info(`[DatapackContext] Storing datapack ${dp.id} with ${dp.data?.length || 0} data points`);
        newMap.set(dp.id, dp);
      });
      logger.info(`[DatapackContext] Stored ${prefetched.length} datapacks. Total in context: ${newMap.size}`);
      return newMap;
    });
  }, []);

  const getDatapack = useCallback((id: string) => {
    return datapacks.get(id);
  }, [datapacks]);

  const clearDatapacks = useCallback(() => {
    setDatapacks(new Map());
    logger.info('[DatapackContext] Cleared all datapacks');
  }, []);

  const value = useMemo(
    () => ({
      datapacks,
      setPrefetchedDatapacks,
      getDatapack,
      clearDatapacks,
    }),
    [clearDatapacks, datapacks, getDatapack, setPrefetchedDatapacks],
  );

  return <DatapackContext.Provider value={value}>{children}</DatapackContext.Provider>;
}

export function useDatapacks() {
  const context = useContext(DatapackContext);
  if (!context) {
    throw new Error('useDatapacks must be used within DatapackProvider');
  }
  return context;
}
